import { AxiosError } from "axios";

interface ProblemDetail {
  detail?: string;
  title?: string;
}

function extractDetail(error: AxiosError): string | null {
  const data = error.response?.data as ProblemDetail | string | undefined;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
    if (typeof data.title === "string" && data.title.trim()) return data.title;
  }
  return null;
}

/** A volume sentence that already says what to do. */
const VOLUME_ADVICE_GIVEN = /\b(wait for|detach (this|the|it)|turn on force)\b/i;

/**
 * The setup mistakes people actually make, and what to do about each.
 *
 * Matched against the upstream OpenStack text, which survives to the client:
 * the SDK wrapper raises ``f"{context}: {exc}"``, so the Neutron/Nova reason
 * is still in the detail after the backend scrubs internal URLs and ids.
 * Since 2026-09-28 the backend also rewrites the cloud's nouns in that text
 * (`error_sanitize.customer_words`: "Keypair" → "SSH key", "floating IP" →
 * "public IP", "Security group rule" → "Firewall rule"), so each pattern
 * matches both spellings.
 *
 * These run before the status-code fallbacks — a 409 saying "one or more ports
 * have an IP allocation" deserves "detach the instances first", not the
 * generic "that name may already be in use". Phrased in console terms (tabs
 * and buttons the user can see), never OpenStack ones.
 *
 * Order matters: first match wins, so keep the specific patterns above the
 * broad ones.
 */
const SETUP_HINTS: ReadonlyArray<{
  match: RegExp;
  action: string;
  /** The sentence already carries this advice (the product wrote it): add
   *  nothing, so it is not said twice. */
  unless?: RegExp;
}> = [
  {
    // Associating a floating IP to an instance whose subnet has no path out.
    // The single most common "why doesn't my server work" setup mistake.
    match: /not reachable from subnet|external network .*not reachable/i,
    action:
      "That subnet has no router with an external gateway. Open Networking → Routers, attach the subnet to a router, and make sure that router has an external network set.",
  },
  {
    match: /one or more ports have an IP allocation|still in use/i,
    action:
      "Detach what's still using it first — instances, load balancers, or a router interface on the Routers tab.",
  },
  {
    match: /no more IP addresses available/i,
    action:
      "The subnet's address pool is full — delete unused ports, or add another subnet to the network.",
  },
  {
    match: /overlaps with another subnet/i,
    action: "Pick a CIDR that doesn't overlap an existing subnet.",
  },
  {
    match: /router already has a port on subnet|already has an interface/i,
    action: "That subnet is already attached to this router.",
  },
  {
    match: /disk is too small|min_?disk|requires more disk/i,
    action:
      "Pick a larger instance size, or an image that fits the one you chose.",
  },
  {
    match: /(keypair|ssh key) .*not found|invalid key_?name/i,
    action:
      "Create an SSH key on the SSH keys page, or pick one that still exists.",
  },
  // The block storage service refuses an action on a volume in one shape of
  // sentence — "status must be available …" — whatever the volume is busy
  // with. The advice follows the state the sentence names; a single "detach
  // it first" for all of them told people to detach a volume that was being
  // backed up, which cannot be detached either (platform test round 4, B3).
  // The backend says these in its own words before the cloud is asked
  // (services/volume_state.py); what is left for these patterns is the race
  // where the state changed in between, and other callers of the same text.
  {
    // The storage service's own state word, hyphen and all: "backing up" in
    // another product's sentence is not about a volume.
    match: /\bbacking-up\b/i,
    action:
      "A backup of this volume is still running — wait for it to finish, then try again.",
    unless: VOLUME_ADVICE_GIVEN,
  },
  {
    match: /\brestoring-backup\b/i,
    action:
      "A backup is being restored into this volume — wait for it to finish, then try again.",
    unless: VOLUME_ADVICE_GIVEN,
  },
  {
    match:
      /(currently|status is:?|volume is|is in)\s+['"]?(extending|retyping|uploading|downloading|attaching|detaching|creating)\b/i,
    action: "The volume is busy with another change — wait for it to finish, then try again.",
    unless: VOLUME_ADVICE_GIVEN,
  },
  {
    match:
      /status must be available[^.]*\bin-use\b|volume .*\b(is|already) (attached|in-use)|still attached/i,
    // By the time this text is read the action's name is gone (the "failed
    // to …" wrapper is dropped), so the one sentence covers both ways out.
    action:
      "Detach the volume from its instance first. For a snapshot or a backup, turning on Force does it while the volume stays in use.",
    unless: VOLUME_ADVICE_GIVEN,
  },
  {
    match: /already associated|already has a (floating|public)/i,
    action:
      "That address already points somewhere — disassociate it before pointing it elsewhere.",
  },
  {
    match: /(security group|firewall) rule already exists/i,
    action: "That rule is already on this firewall.",
  },
  {
    match: /no subnets? on|network .*has no subnet/i,
    action: "Add a subnet to the network before launching onto it.",
  },
];

/**
 * Drop the backend's "failed to <verb> <resource>" wrapper when a real reason
 * follows it. The caller's own fallback ("Failed to delete subnet") already
 * says which action failed, so the prefix is a second copy of that plus a raw
 * id — it pushes the actual reason off the end of the toast.
 */
function stripWrapperPrefix(detail: string): string {
  const match = /^failed to [^:]{0,80}:\s*(?<reason>.+)$/is.exec(detail);
  const reason = match?.groups?.["reason"]?.trim();
  return reason ? reason : detail;
}

/**
 * Unwrap pydantic's field-error envelope.
 *
 * A 422 from a field validator arrives as `"password: Value error, password
 * must be at least 12 characters"` — the field name and the words "Value
 * error" are framework noise in front of a sentence that was written for a
 * human. Keep the sentence.
 */
function stripPydanticPrefix(detail: string): string {
  const match = /^[a-z_][a-z0-9_.]{0,60}:\s*value error,\s*(?<reason>.+)$/is.exec(
    detail,
  );
  const reason = match?.groups?.["reason"]?.trim();
  return reason ? reason : detail;
}

/** PG1-F6 (B27): a 503 whose reason is a missing configuration ("Central logging is
 *  not configured on this cloud") is not a passing fault: say what to do, not "retry". */
const CONFIG_REASON = /\bnot (configured|set up|switched on|turned on|enabled)\b/i;
/** The reason names the whole cloud: only the provider can change it. */
const PROVIDER_SCOPE = /\bon this (cloud|deployment)\b/i;
export const PROVIDER_SETUP_ACTION =
  "Your cloud provider has to set this up first; it works once they have, and retrying will not change it.";
export const SET_UP_FIRST_ACTION = "Set this up first; retrying will not change it.";

/**
 * A concrete next step for common failure classes — turns "what went wrong"
 * into "what to do". Returns null when the detail already speaks for itself
 * (e.g. a 400/422 validation message).
 */
function suggestedAction(error: AxiosError, message: string): string | null {
  if (!error.response) return "Check your connection and try again.";
  const hint = SETUP_HINTS.find((h) => h.match.test(message));
  if (hint) return hint.unless?.test(message) ? null : hint.action;
  if (/quota/i.test(message)) {
    return "Free up resources or request a quota increase.";
  }
  const status = error.response.status;
  if (status === 401) return "Your session may have expired — sign in again.";
  if (status === 403) return "You may not have permission for this action.";
  if (status === 404) {
    return "It may have already been deleted — refresh and try again.";
  }
  if (status === 409) {
    // Signing up with an address that already has an account is a sign-in,
    // not a naming problem — the generic "try a different one" sends people
    // to create a second account they don't need.
    if (/account with this email already exists/i.test(message)) {
      return "Sign in instead, or use the password reset link if you've forgotten it.";
    }
    // 409 covers both name collisions and "resource is busy in another state"
    // (e.g. Nova rejecting an action while task_state is powering-off). Don't
    // blindly blame the name.
    if (/task_state|while it is|in progress|currently|pending|transition/i.test(message)) {
      return "It's still finishing another action — wait a few seconds and retry.";
    }
    if (/already (exists|in use)|duplicate|name/i.test(message)) {
      return "That name may already be in use — try a different one.";
    }
    return null;
  }
  if (status === 429) return "Too many requests — wait a moment and retry.";
  // PG1-F6 (B27): a 503 that says something is not set up will not pass on a retry
  if (status === 503 && CONFIG_REASON.test(message)) {
    return PROVIDER_SCOPE.test(message) ? PROVIDER_SETUP_ACTION : SET_UP_FIRST_ACTION;
  }
  if (status >= 500) return "Something went wrong on our side — retry in a moment.";
  return null;
}

/**
 * Build a user-facing error message: the backend's RFC-7807 `detail`/`title`
 * (the real reason), plus a suggested next action for common failure classes.
 * Every mutation funnels through here, so error UX improves app-wide.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof AxiosError)) return fallback;
  const base = stripPydanticPrefix(
    stripWrapperPrefix(extractDetail(error) ?? fallback),
  );
  const action = suggestedAction(error, base);
  return action ? `${base} — ${action}` : base;
}
